import React, { useState, useEffect } from "react";
import {
  UserPlus,
  Trophy,
  AtSign,
  Heart,
  MessageCircle,
  Bell,
  Church,
  BookOpen,
  Cross,
  HandHeart,
  CheckCheck,
  ArrowLeft,
  FileText,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../services/api";

// ─── Type → icon map ──────────────────────────────────────────────────────────
const typeIconMap = {
  user_registered: { icon: UserPlus },
  comment_on_post: { icon: MessageCircle },
  community_post:  { icon: FileText },
  like:            { icon: Heart },
  follow:          { icon: UserPlus },
  mention:         { icon: AtSign },
  trophy:          { icon: Trophy },
  prayer:          { icon: HandHeart },
  church:          { icon: Church },
  bible:           { icon: BookOpen },
  cross:           { icon: Cross },
  system:          { icon: Bell },
};

const getIcon = (type) => typeIconMap[type] || { icon: Bell };

// ─── Relative time helper ─────────────────────────────────────────────────────
const formatRelativeTime = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// ─── Group by date label ──────────────────────────────────────────────────────
const groupByDate = (items) => {
  const todayMs    = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
  const yesterdayMs = todayMs - 86400000;

  return items.reduce((groups, notif) => {
    const d      = new Date(notif.created_at);
    const dayMs  = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const label  =
      dayMs === todayMs    ? "Today"
      : dayMs === yesterdayMs ? "Yesterday"
      : d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

    if (!groups[label]) groups[label] = [];
    groups[label].push(notif);
    return groups;
  }, {});
};

// ─── Avatar with initials fallback ───────────────────────────────────────────
// const BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");


const Avatar = ({ src, name }) => {
  const [imgError, setImgError] = useState(false);
  const fullSrc = src?.startsWith("http") ? src : `${API_ROOT}${src}`;

  if (!imgError && src && !src.includes("user_default")) {
    return (
      <img
        src={fullSrc}
        alt={name}
        onError={() => setImgError(true)}
        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
      />
    );
  }

  const initials = name
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="w-10 h-10 rounded-full bg-[#1F3A60] text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
      {initials || "?"}
    </div>
  );
};

// ─── Single notification row ──────────────────────────────────────────────────
const NotificationRow = React.memo(({ notif, isLast, onRead }) => {
  const { icon: IconComponent } = getIcon(notif.type);
  const isUnread = notif.is_read === 0;

  return (
    <div
      onClick={() => isUnread && onRead(notif.id)}
      className={`relative flex items-start gap-3 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-[#F8FAFC] transition-all cursor-pointer
        ${isUnread ? "bg-[#1F3A60]/[0.02]" : ""}
        ${!isLast ? "border-b border-[#F1F4F8]" : ""}`}
    >
      {isUnread && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 bg-[#1F3A60] rounded-r-full" />
      )}

      <div className="relative flex-shrink-0">
        <Avatar src={notif.avatar} name={notif.name} />
        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#1F3A60] text-white flex items-center justify-center shadow-sm">
          <IconComponent size={11} />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#334155] leading-relaxed">
          <span className="font-semibold text-[#0F1D2E]">{notif.name}</span>
          <span className="text-[#7B8794] mx-1.5">•</span>
          <span className="text-[#526071]">{notif.message}</span>
        </p>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5">
          <span className="text-xs text-[#94A3B8]">{formatRelativeTime(notif.created_at)}</span>
          {isUnread && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#1F3A60]/10 text-[#1F3A60]">
              New
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

NotificationRow.displayName = "NotificationRow";

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AdminNotificationPage() {
  const queryClient = useQueryClient();
  const QUERY_KEY   = "admin-notifications";

  const [page, setPage]               = useState(1);
  const [allNotifs, setAllNotifs]     = useState([]);
  const [hasNext, setHasNext]         = useState(false);
  const [totalCount, setTotalCount]   = useState(0);

  // ── Fetch current page ────────────────────────────────────────────────────
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [QUERY_KEY, page],
    queryFn:  () => api.fetchAdminNotifications(page),
    keepPreviousData: true,
  });

  // Accumulate pages into allNotifs (append on page increment)
  useEffect(() => {
    if (!data?.data) return;
    setAllNotifs((prev) =>
      page === 1 ? data.data : [...prev, ...data.data]
    );
    setHasNext(data.pagination?.hasNext ?? false);
    setTotalCount(data.pagination?.total ?? 0);
  }, [data, page]);

  const unreadCount = allNotifs.filter((n) => n.is_read === 0).length;
  const grouped     = groupByDate(allNotifs);

  // ── Mark one as read ──────────────────────────────────────────────────────
  const { mutate: markOneRead } = useMutation({
    mutationFn: (id) => api.markNotificationRead(id),
    onSuccess: (_, id) => {
      // update local accumulated list instantly
      setAllNotifs((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  // ── Mark all as read ──────────────────────────────────────────────────────
  const { mutate: markAllRead, isPending: isMarkingAll } = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      setAllNotifs((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
  
  const handleLoadMore = () => {
    if (hasNext && !isFetching) setPage((p) => p + 1);
  };

  // ── Loading state (first page only) ──────────────────────────────────────
  if (isLoading && page === 1) {
    return (
      <div className="bg-[#f8fafc] min-h-[calc(100vh-56px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-[#1F3A60]" />
          <p className="text-sm text-[#7B8794]">Loading notifications…</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-[#f8fafc] min-h-[calc(100vh-56px)] flex items-center justify-center">
        <div className="bg-white rounded-2xl border border-[#E8EDF3] p-8 text-center max-w-sm">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bell size={24} className="text-red-400" />
          </div>
          <h3 className="text-base font-semibold text-[#0F1D2E] mb-1">Failed to load</h3>
          <p className="text-sm text-[#7B8794]">{error?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f8fafc] min-h-[calc(100vh-56px)] overflow-y-auto">
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to="/dashboard"
              className="p-2 hover:bg-white rounded-full transition-colors lg:hidden flex-shrink-0"
            >
              <ArrowLeft size={20} className="text-[#526071]" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-[20px] sm:text-[28px] font-bold text-[#0F1D2E] tracking-tight">
                Notifications
              </h1>
              <p className="text-xs sm:text-sm text-[#7B8794] mt-0.5 truncate">
                {unreadCount > 0
                  ? `${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`
                  : "All caught up!"}
              </p>
            </div>
          </div>

          {unreadCount > 0 && (
            <button
              onClick={() => markAllRead()}
              disabled={isMarkingAll}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-[#1F3A60] bg-white rounded-xl hover:bg-gray-50 transition-all border border-[#E8EDF3] disabled:opacity-60"
            >
              {isMarkingAll
                ? <Loader2 size={16} className="animate-spin" />
                : <CheckCheck size={16} />}
              <span className="hidden md:inline">Mark all as read</span>
            </button>
          )}
        </div>

        {/* Notifications */}
        <div className="space-y-6 pb-6">
          {allNotifs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E8EDF3] p-8 sm:p-12 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell size={28} className="text-[#94A3B8]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F1D2E] mb-1">No notifications</h3>
              <p className="text-sm text-[#7B8794]">You're all caught up. Check back later.</p>
            </div>
          ) : (
            Object.entries(grouped).map(([label, items]) => (
              <div key={label}>
                <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-widest mb-2 px-1">
                  {label}
                </p>
                <div className="bg-white rounded-2xl border border-[#E8EDF3] overflow-hidden shadow-sm">
                  {items.map((notif, idx) => (
                    <NotificationRow
                      key={notif.id}
                      notif={notif}
                      isLast={idx === items.length - 1}
                      onRead={markOneRead}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Load more */}
        {allNotifs.length > 0 && (
          <div className="mt-2 sm:mt-4 text-center pb-4 space-y-3">
            <p className="text-xs text-[#94A3B8]">
              Showing {allNotifs.length} of {totalCount} notification{totalCount !== 1 ? "s" : ""}
            </p>
            {hasNext && (
              <button
                onClick={handleLoadMore}
                disabled={isFetching}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-[#1F3A60] bg-white border border-[#E8EDF3] rounded-xl hover:bg-gray-50 transition-all hover:shadow-sm disabled:opacity-60"
              >
                {isFetching
                  ? <><Loader2 size={15} className="animate-spin" />Loading…</>
                  : "Load more"}
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}